import { FC, useCallback, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import { IImageProps } from "apps/website/components/base/Image/Image";
import Card from "apps/website/components/feature/Card/Card";
import { Theme } from "apps/website/maps/Theme.map";
import { WithTestID } from "apps/website/types";
import Grid from "apps/website/components/layout/Grid/Grid";
import Column from "apps/website/components/layout/Column/Column";
import Button from "apps/website/components/base/Button/Button";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { ITagProps } from "apps/website/components/base/Tag/Tag";

import TimeToComplete, {
  ITimeToCompleteProps,
} from "../../TimeToComplete/TimeToComplete";

export enum ESurveyState {
  UNLOCKED,
  LOCKED,
  COMING_SOON,
}
export interface ISurveyCardProps extends WithTestID {
  title: string;
  description: string;
  image?: IImageProps;
  link?: string;
  theme?: Theme;
  closes?: Dayjs;
  state?: ESurveyState;
  timeToComplete?: ITimeToCompleteProps;
  blockedByNote?: string;
  linkText?: string;
}
const SurveyCard: FC<ISurveyCardProps> = ({
  title,
  description,
  image,
  link,
  closes,
  theme = "light",
  state = ESurveyState.UNLOCKED,
  "data-testid": testId,
  timeToComplete,
  blockedByNote,
  linkText = "Take part",
}) => {

  const cardFooter = useCallback(() => (
    <>
      <Grid>
        <Column justify="center" align="center">
          <Spacer size="md" />
          { state === ESurveyState.UNLOCKED ? (
            <>
              <Button to={ link } withIcon color="secondary" data-testid="SurveyCardButton">{ linkText }</Button>
              { timeToComplete ? (
                <>
                  <Spacer size="md" />
                  <TimeToComplete isApprox={false} theme="default" hours={ timeToComplete.hours } minutes={ timeToComplete.minutes } />
                </>
              ) : null }
              { closes ? (
                <>
                  <Spacer size="md" />
                  <TextBody size={ 2 } align="center" data-testid="SurveyCardClosesCopy">Closes { dayjs(closes).format("DD MMMM YYYY") }</TextBody>
                </>
              ) : null }
            </>
          ) : (
            <>
              <Button disabled>{ state === ESurveyState.LOCKED ? "Survey locked" : "Coming soon" }</Button>
              <Spacer size="md" />
              <TextBody size={ 2 } align="center">{ state === ESurveyState.LOCKED ? blockedByNote ? `Complete ${blockedByNote} survey to unlock` : <>&nbsp;</> : "Check back soon" }</TextBody>
            </>
          ) }

        </Column>
      </Grid>
    </>
  ), [ state ]);

  const tag = useMemo((): ITagProps => {
    if (state === ESurveyState.COMING_SOON || state === ESurveyState.LOCKED) return {
      theme: "light-grey",
      text: state === ESurveyState.COMING_SOON ? "Coming soon" : "Survey Locked",
      icon: "padlock",
    };
    return {
      theme: "green",
      text: "Open",
      icon: "tickHeavy",
    };
  }, [ state ]);

  return (
    <Card
      data-testid={`survey-card-${testId}`}
      component="SurveyCard"
      title={title}
      link={link}
      description={description}
      image={image}
      theme={theme}
      ctaAction="link"
      Footer={cardFooter}
      divider="none"
      align="center"
      tag={tag}
      tagPosition="hugBorderLeft"
      className="rounded-xl overflow-hidden"
    />
  );
};

export default SurveyCard;
